import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheckCircle, 
  faInfoCircle,
  faUserPlus,
  faUsers,
  faPause,
  faClock,
  faPlay,
  faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import AssignInterviewersModal from './components/AssignInterviewersModal';
import '../quota/QuotaBuilder.css';
import { useAuth } from '../../contexts/AuthContext';

const SupervisorQuotaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignModalCellId, setAssignModalCellId] = useState(null);
  const [selectedQuotaIds, setSelectedQuotaIds] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  // Get current supervisor info from auth context (for self-assignment)
  const currentSupervisor = user && user.role === 'superviseur' ? {
    id: user.id,
    name: user.name,
    email: user.email,
    ref: user.ref
  } : null;

  useEffect(() => {
    fetchQuotaDetail();
  }, [id]);

  const fetchQuotaDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/quotas/my-quotas');
      if (response.data.success) {
        const foundProject = response.data.data.find(p => p.project_id == id);
        if (foundProject) {
          setProject(foundProject);
        } else {
          setError('Projet non trouvé');
        }
      } else {
        setError('Erreur lors du chargement des quotas');
      }
    } catch (error) {
      console.error('Error fetching quota detail:', error);
      setError('Erreur lors du chargement des quotas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleExportExcel = async () => {
    if (!project || !project.quotas || project.quotas.length === 0) {
      return;
    }

    try {
      setExporting(true);
      const response = await axios.get(`/quotas/my-quotas/${id}/export`, {
        responseType: 'blob',
      });

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `mes_quotas_${project.survey?.title || id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Erreur lors de l\'exportation. Veuillez réessayer.');
    } finally {
      setExporting(false);
    }
  };

  // Get status badge for quota cell
  const getStatusBadge = (quota) => {
    const status = quota.status || quota.auto_status || 'pending';
    
    if (status === 'paused' || quota.is_paused) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
          <FontAwesomeIcon icon={faPause} />
          En pause
        </span>
      );
    }
    
    if (status === 'completed' || quota.is_complete) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
          <FontAwesomeIcon icon={faCheckCircle} />
          Terminé
        </span>
      );
    }
    
    if (status === 'in_progress') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
          <FontAwesomeIcon icon={faPlay} />
          En cours
        </span>
      );
    }
    
    // pending
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
        <FontAwesomeIcon icon={faClock} />
        En attente
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-600 mb-4" />
          <p className="text-gray-600">Chargement des quotas...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="text-4xl text-red-600 mb-4" />
          <p className="text-gray-600">{error || 'Projet non trouvé'}</p>
          <button
            onClick={() => navigate('/supervisor/my-quotas')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-3 sm:p-6">
        <header className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 break-words">
            {project.survey.title}
            {project.survey_wave?.label && (
              <span className="ml-2 inline-block align-middle px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                {project.survey_wave.label}
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Détails des quotas assignés</p>
        </header>

        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {/* Survey Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 sm:p-6 text-white">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 break-words">
                  {project.survey.title}
                  {project.survey_wave?.label && (
                    <span className="ml-2 inline-block align-middle px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/25 text-white">
                      {project.survey_wave.label}
                    </span>
                  )}
                </h2>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm opacity-90">
                  <span>
                    <strong>ID LimeSurvey:</strong> {project.survey.limesurvey_id}
                  </span>
                  {project.survey?.price_per_complete != null && (
                    <span>
                      <strong>Prix par complétion:</strong> {Number(project.survey.price_per_complete).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                    </span>
                  )}
                  {project.fieldwork_start_date && (
                    <span>
                      <strong>Début:</strong> {formatDate(project.fieldwork_start_date)}
                    </span>
                  )}
                  {project.fieldwork_end_date && (
                    <span>
                      <strong>Fin:</strong> {formatDate(project.fieldwork_end_date)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  onClick={handleExportExcel}
                  disabled={exporting || !project.quotas || project.quotas.length === 0}
                  className="px-3 py-1.5 bg-white hover:bg-gray-50 disabled:bg-gray-100 text-red-600 disabled:text-red-300 rounded-lg flex items-center justify-center gap-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
                  title="Exporter en Excel"
                >
                  <FontAwesomeIcon icon={exporting ? faSpinner : faFileExcel} className={exporting ? 'animate-spin' : ''} />
                  <span>{exporting ? 'Export...' : 'Exporter'}</span>
                </button>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-white text-center whitespace-nowrap ${
                  project.project_status === 'locked' 
                    ? 'text-green-600' 
                    : project.project_status === 'in_progress'
                    ? 'text-blue-600'
                    : 'text-gray-600'
                }`}>
                  {project.project_status === 'locked' ? 'Ouvert' : 
                   project.project_status === 'in_progress' ? 'En cours' : 
                   project.project_status === 'review' ? 'En révision' :
                   'Brouillon'}
                </span>
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="p-3 sm:p-6 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Quotas assignés</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900">{project.total_quotas}</div>
              </div>
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Mon quota assigné</div>
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{(project.my_total_assigned || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Mes complétés</div>
                <div className="text-xl sm:text-2xl font-bold text-green-600">{(project.my_total_completes || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">Restants</div>
                <div className="text-xl sm:text-2xl font-bold text-orange-600">{(project.my_total_remaining || 0).toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Quotas List */}
          <div className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                Détails des quotas assignés ({project.quotas.length})
              </h3>
              {/* Show bulk assign button only if NO quota has any interviewer assignment yet */}
              {project.quotas.length > 0 && !project.quotas.some(q => 
                q.interviewer_assignments && q.interviewer_assignments.length > 0
              ) && (
                <button
                  onClick={() => {
                    const allIds = project.quotas.map(q => q.id);
                    setSelectedQuotaIds(allIds);
                    setShowAssignModal(true);
                  }}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
                >
                  <FontAwesomeIcon icon={faUsers} />
                  <span>Assigner en masse</span>
                </button>
              )}
            </div>
            
            {project.quotas.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-gray-500">
                <p className="text-sm sm:text-base">Aucun quota assigné pour cette enquête</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {project.quotas.map((quota, index) => (
                  <div 
                    key={quota.id}
                    className={`bg-white border rounded-lg p-3 sm:p-5 hover:shadow-md transition-shadow relative ${
                      quota.is_paused || quota.status === 'paused' 
                        ? 'border-yellow-300 bg-yellow-50/30' 
                        : 'border-gray-200'
                    }`}
                  >
                    {/* Status Badge - positioned absolutely at top right */}
                    <div className="absolute top-3 right-3">
                      {getStatusBadge(quota)}
                    </div>
                    
                    <div className="pr-20 sm:pr-0">
                      {/* Quota Number - same format as CellDetailPanel */}
                      <div className="mb-3 sm:mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Quota {index + 1}</label>
                        <div className="dimension-values">
                          {quota.dimension_values_labels_json && quota.dimension_values_labels_json.length > 0 ? (
                            quota.dimension_values_labels_json.map((dimLabel, idx) => (
                              <div 
                                key={idx} 
                                className="dimension-value-item"
                              >
                                <span className="dim-label">{dimLabel.dimension_label}:</span>
                                <span className="dim-value">{dimLabel.value_label}</span>
                              </div>
                            ))
                          ) : (
                            <div className="text-gray-500 text-xs sm:text-sm">Aucune dimension disponible</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar and Stats */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="text-gray-600">Progression</span>
                        <span className="font-semibold text-gray-900">{quota.my_progress_percentage || 0}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${
                            (quota.my_completes || 0) >= (quota.my_assigned_count || 0) ? 'bg-green-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(100, quota.my_progress_percentage || 0)}%` }}
                        ></div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-2 border-t border-gray-100">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Mon quota</div>
                          <div className="text-base sm:text-lg font-semibold text-gray-900">{(quota.my_assigned_count || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Complétés</div>
                          <div className="text-base sm:text-lg font-semibold text-green-600">{(quota.my_completes || 0).toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Restants</div>
                          <div className="text-base sm:text-lg font-semibold text-orange-600">{(quota.my_remaining || 0).toLocaleString()}</div>
                        </div>
                      </div>

                      {quota.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">Notes</div>
                          <div className="text-xs sm:text-sm text-gray-700 break-words">{quota.notes}</div>
                        </div>
                      )}

                      {/* Interviewer/Supervisor Assignments */}
                      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-3">
                          <div className="text-xs sm:text-sm font-semibold text-gray-700">
                            Enquêteurs assignés
                            {(quota.interviewer_assignments?.length || 0) > 0 && (
                              <span className="ml-2 text-gray-500 font-normal">
                                ({(quota.interviewer_assignments?.length || 0)})
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setAssignModalCellId(quota.id);
                              setShowAssignModal(true);
                            }}
                            className="text-xs sm:text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-1.5 whitespace-nowrap w-full sm:w-auto"
                          >
                            <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
                            <span>{(quota.interviewer_assignments?.length || 0) > 0 ? 'Modifier' : 'Assigner'}</span>
                          </button>
                        </div>
                        
                        {/* Interviewer Assignments */}
                        {quota.interviewer_assignments && quota.interviewer_assignments.length > 0 ? (
                          <div className="space-y-2">
                            {quota.interviewer_assignments.map((assignment) => {
                              const completes = assignment.completes || 0;
                              const assigned = assignment.assigned_count || 0;
                              const isComplete = assigned > 0 && completes >= assigned;
                              const isSupervisorSelf = assignment.is_supervisor_self || (assignment.supervisor && !assignment.interviewer);
                              
                              return (
                                <div 
                                  key={assignment.id}
                                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 sm:p-2 bg-gray-50 rounded text-xs sm:text-sm"
                                >
                                  <div className="flex-1 flex flex-wrap items-center gap-1 sm:gap-2">
                                    {isSupervisorSelf ? (
                                      <>
                                        <span className="font-medium">{assignment.supervisor?.name || user?.name || 'Prestataire'}</span>
                                        <span className="text-gray-500">(moi)</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="font-medium">{assignment.interviewer?.name || 'Enquêteur'}</span>
                                        {assignment.interviewer?.ref && (
                                          <span className="text-gray-500">({assignment.interviewer.ref})</span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className={`font-semibold ${isComplete ? 'text-green-600' : completes > 0 ? 'text-blue-600' : 'text-gray-500'}`}>
                                      {completes}/{assigned}
                                    </span>
                                    {isComplete && (
                                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (!quota.my_assigned_count || quota.my_assigned_count === 0) && (
                          <div className="text-xs sm:text-sm text-gray-500 italic">
                            Aucune personne assignée
                          </div>
                        )}

                        {/* Team members who worked here without an assignment (counted in Complétés) */}
                        {quota.unassigned_team_completes && quota.unassigned_team_completes.length > 0 && (
                          <div className="space-y-2 mt-2">
                            {quota.unassigned_team_completes.map((entry) => (
                              <div
                                key={`unassigned-${entry.interviewer.id}`}
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 sm:p-2 bg-amber-50 rounded text-xs sm:text-sm"
                              >
                                <div className="flex-1 flex flex-wrap items-center gap-1 sm:gap-2">
                                  <span className="font-medium">{entry.interviewer.name}</span>
                                  {entry.interviewer.ref && (
                                    <span className="text-gray-500">({entry.interviewer.ref})</span>
                                  )}
                                  <span className="text-amber-700 italic">non assigné</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="font-semibold text-blue-600">{entry.completes}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Interviewers Modal */}
      {showAssignModal && (
        <AssignInterviewersModal
          show={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setAssignModalCellId(null);
            setSelectedQuotaIds([]);
          }}
          quotaProjectId={project.project_id}
          quotaCellIds={assignModalCellId ? assignModalCellId : selectedQuotaIds}
          targetCounts={
            assignModalCellId
              ? (project.quotas.find(q => q.id === assignModalCellId)?.my_assigned_count || 0)
              : selectedQuotaIds.map(id => (project.quotas.find(q => q.id === id)?.my_assigned_count || 0))
          }
          onSuccess={() => {
            fetchQuotaDetail();
          }}
          currentSupervisor={currentSupervisor}
          existingAssignments={
            assignModalCellId
              ? (() => {
                  const quota = project.quotas.find(q => q.id === assignModalCellId);
                  const interviewerAssignments = quota?.interviewer_assignments || [];
                  
                  // Separate interviewer assignments and supervisor self-assignments
                  const interviewerAssigns = interviewerAssignments.filter(a => !a.is_supervisor_self && a.interviewer);
                  const supervisorAssigns = interviewerAssignments.filter(a => a.is_supervisor_self || (a.supervisor && !a.interviewer));
                  
                  return {
                    interviewer_assignments: interviewerAssigns,
                    supervisor_assignments: supervisorAssigns
                  };
                })()
              : (() => {
                  // Aggregate existing assignments for bulk mode
                  const aggregatedInterviewers = {};
                  const aggregatedSupervisors = {};
                  
                  selectedQuotaIds.forEach(qid => {
                    const quota = project.quotas.find(q => q.id === qid);
                    if (!quota) return;
                    
                    // Aggregate interviewer assignments
                    quota.interviewer_assignments?.forEach(a => {
                      if (a.is_supervisor_self || (a.supervisor && !a.interviewer)) {
                        // Supervisor self-assignment
                        const key = a.supervisor?.id;
                        if (key && !aggregatedSupervisors[key]) {
                          aggregatedSupervisors[key] = {
                            ...a,
                            assigned_count: 0,
                            completes: 0
                          };
                        }
                        if (key) {
                          aggregatedSupervisors[key].assigned_count += (a.assigned_count || 0);
                          aggregatedSupervisors[key].completes += (a.completes || 0);
                        }
                      } else if (a.interviewer) {
                        // Regular interviewer assignment
                        const key = a.interviewer.id;
                        if (!aggregatedInterviewers[key]) {
                          aggregatedInterviewers[key] = {
                            ...a,
                            assigned_count: 0,
                            completes: 0
                          };
                        }
                        aggregatedInterviewers[key].assigned_count += (a.assigned_count || 0);
                        aggregatedInterviewers[key].completes += (a.completes || 0);
                      }
                    });
                  });
                  
                  return {
                    interviewer_assignments: Object.values(aggregatedInterviewers),
                    supervisor_assignments: Object.values(aggregatedSupervisors),
                    isBulkAggregated: true
                  };
                })()
          }
        />
      )}
    </div>
  );
};

export default SupervisorQuotaDetail;

